import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BladeId } from '@/services/blades/bladeRegistry';

/**
 * Offline Blade Session — a cached calculation result stored in IndexedDB
 * for offline access.
 */
export interface OfflineBladeSession {
  id: string;
  toolName: string;
  bladeId: BladeId;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  headline: string;
  detail: string;
  timestamp: number;
  expiresAt: number;
}

interface OfflineBladeState {
  sessions: OfflineBladeSession[];
  isOffline: boolean;

  // Cache a blade calculation result
  cacheSession: (session: Omit<OfflineBladeSession, 'id' | 'timestamp' | 'expiresAt'>) => void;

  // Retrieve a cached session by blade ID
  getSession: (bladeId: BladeId) => OfflineBladeSession | null;

  // Get all cached sessions for a tool
  getSessionsByTool: (toolName: string) => OfflineBladeSession[];

  // Clear an expired or invalid session
  clearSession: (id: string) => void;

  // Clear all expired sessions
  clearExpiredSessions: () => void;

  // Set offline status
  setOffline: (offline: boolean) => void;

  // Clear all offline cache
  clearAllSessions: () => void;
}

// 24-hour TTL for offline cache
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const useOfflineBlade = create<OfflineBladeState>()(
  persist(
    (set, get) => ({
      sessions: [],
      isOffline: false,

      cacheSession: (session) => {
        const now = Date.now();
        const id = `${session.bladeId}-${now}`;
        const newSession: OfflineBladeSession = {
          ...session,
          id,
          timestamp: now,
          expiresAt: now + CACHE_TTL_MS,
        };

        set((state) => {
          // Keep the 10 most-recent sessions per blade, NEWEST FIRST.
          // Previously the new session was appended after the existing
          // ones and `.slice(0, 9)` retained the OLDEST 9 — so getSession()
          // (which uses .find(), returning the first match) surfaced the
          // stalest cached result instead of the one the user just saved,
          // with a wrongly-inflated "cached X minutes ago". Prepend the new
          // session and slice from the front so the newest is always first
          // (matching toolBasketStore's newest-retention intent).
          const otherSessions = state.sessions.filter((s) => s.bladeId !== session.bladeId);
          const sameBlade = state.sessions.filter((s) => s.bladeId === session.bladeId);
          const kept = [newSession, ...sameBlade].slice(0, 10);

          return {
            sessions: [...kept, ...otherSessions],
          };
        });
      },

      getSession: (bladeId) => {
        const state = get();
        const session = state.sessions.find((s) => s.bladeId === bladeId && s.expiresAt > Date.now());
        return session || null;
      },

      getSessionsByTool: (toolName) => {
        const state = get();
        return state.sessions.filter((s) => s.toolName === toolName && s.expiresAt > Date.now());
      },

      clearSession: (id) => {
        set((state) => ({
          sessions: state.sessions.filter((s) => s.id !== id),
        }));
      },

      clearExpiredSessions: () => {
        const now = Date.now();
        set((state) => ({
          sessions: state.sessions.filter((s) => s.expiresAt > now),
        }));
      },

      setOffline: (offline) => {
        set({ isOffline: offline });
      },

      clearAllSessions: () => {
        set({ sessions: [] });
      },
    }),
    {
      name: 'offline-blade-store',
      version: 1,
    }
  )
);
